
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import {
    CircleMode,
    DragCircleMode,
    DirectMode,
    SimpleSelectMode
} from 'mapbox-gl-draw-circle';
import CutPolygonMode from 'mapbox-gl-draw-cut-polygon-mode';
import mapboxGlDrawPassingMode from 'mapbox-gl-draw-passing-mode';
import DrawStyle from "./DrawStyle";

const DrawStatus = {
    NOTSET: "notset",
    DRAWING: "drawing"
};

export const DrawTools = {
    POLYGON: "polygon",
    CIRCLE: "circle",
    POINT: "point",
    UPLOAD_FILE: "uploadFile"
};

class DrawState {
    constructor() {
        this.map = null;
        this.mapState = null;
        this.draw = null;

        this.status = DrawStatus.NOTSET;
        this._canTrashVertices = false;
        this._mode = null;
        this.enabledTools = [];
        this.keepModeAfterCreate = false;
        this.onCreate = null;
        this.onUpdate = null;
        this.onDelete = null;
        this.suppressNextCreate = false;
        this.suppressCreateTimer = null;
        this.drawCursorElement = null;
        this.drawCursorMoveHandler = null;
        this.drawCursorEnterHandler = null;
        this.drawCursorLeaveHandler = null;
    }

    init(mapState, map) {
        this.map = map;
        this.mapState = mapState;
        this.draw = new MapboxDraw({
            displayControlsDefault: false,
            styles: DrawStyle,
            modes: Object.assign({
                draw_circle: CircleMode,
                drag_circle: DragCircleMode,
                direct_select: DirectMode,
                simple_select: SimpleSelectMode,
                static: StaticMode,
                cutPolygonMode: CutPolygonMode,
                passing_mode_polygon: mapboxGlDrawPassingMode(
                    MapboxDraw.modes.draw_polygon
                ),
            }, MapboxDraw.modes)
        });
        map.addControl(this.draw);
        this._setEvents();
    }

    start({ tools, onCreate, onUpdate, onDelete, keepModeAfterCreate = false }) {
        this.status = DrawStatus.DRAWING;
        this.enabledTools = tools;
        this.keepModeAfterCreate = keepModeAfterCreate;
        this.onCreate = onCreate;
        this.onUpdate = onUpdate;
        this.onDelete = onDelete;
    }

    end() {
        this.status = DrawStatus.NOTSET;
        this.enabledTools = [];
        this.keepModeAfterCreate = false;
        this.onCreate = null;
        this.onUpdate = null;
        this._canTrashVertices = false;
        this._mode = null;
        this.clearCreateSuppression();

        this.disableDrawCursor();
    }

    forceCallback(arg1, arg2) {
        if (this.onCreate != null) {
            this.onCreate(arg1, arg2);
        }
    }

    get(featureId) {
        return this.draw.get(featureId);
    }

    add(polygon) {
        return this.draw.add(polygon)[0];
    }

    onDraw() {
        const $this = this;
        return (e) => {
            if (e.type == 'draw.create') {
                if ($this.suppressNextCreate) {
                    $this.clearCreateSuppression();
                    const features = Array.isArray(e.features) ? e.features : [];
                    features.forEach((feature) => {
                        if (feature?.id != null) {
                            $this.draw.delete(feature.id);
                        }
                    });
                    return;
                }

                if (typeof $this.onCreate == 'function') {
                    $this.onCreate(e);
                }
                if ($this.keepModeAfterCreate && $this._mode != null) {
                    setTimeout(() => {
                        if ($this.status == DrawStatus.DRAWING && $this._mode != null) {
                            $this.mode($this._mode);
                        }
                    }, 0);
                }
            } else if (e.type == 'draw.update') {
                if (typeof $this.onUpdate == 'function') {
                    $this.onUpdate(e);
                }
            }
        };
    };

    onDeleteEvent() {
        const $this = this;
        return (e) => {
            if (e.type == 'draw.delete') {
                if (typeof $this.onDelete == 'function') {
                    $this.onDelete(e);
                }
            }
        };
    };

    _setEvents() {
        const $this = this;
        this.map.on('dblclick', e => {
            if ($this._canTrashVertices) {
                $this.draw.trash()
            }
        });
        this.map.on('draw.modechange', (e) => {
            if (e.mode.simple_select == 'simple_select') {
                $this.disableDrawCursor();
            }
        });
        this.map.on('draw.create', this.onDraw());
        this.map.on('draw.update', this.onDraw());
        this.map.on('draw.delete', this.onDeleteEvent());
        this.map.on('draw.actionable', (e) => {
            if (e.actions.trash) {
                $this._canTrashVertices = true;
            }
        });
        this.map.on('draw.modechange', (e) => {
            if (e.mode.simple_select == 'simple_select') {
                $this.disableDrawCursor();
            }
        });
    }

    getCanvasContainer() {
        return this.map?.getCanvasContainer?.() || this.map?.getCanvas?.()?.parentElement || null;
    }

    ensureDrawCursorElement() {
        if (this.drawCursorElement != null) {
            return this.drawCursorElement;
        }

        const cursor = document.createElement('div');
        cursor.setAttribute('aria-hidden', 'true');
        cursor.innerHTML = [
            '<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">',
            '<path d="M12.5 1V24M1 12.5H24" stroke="white" stroke-width="4" stroke-linecap="round"/>',
            '<path d="M12.5 1V24M1 12.5H24" stroke="#111827" stroke-width="2" stroke-linecap="round"/>',
            '<circle cx="12.5" cy="12.5" r="1.8" fill="#111827" stroke="white" stroke-width="1"/>',
            '</svg>'
        ].join('');

        cursor.style.display = 'none';
        cursor.style.height = '25px';
        cursor.style.left = '0';
        cursor.style.pointerEvents = 'none';
        cursor.style.position = 'absolute';
        cursor.style.top = '0';
        cursor.style.transform = 'translate(-50%, -50%)';
        cursor.style.width = '25px';
        cursor.style.zIndex = '5';

        this.drawCursorElement = cursor;
        return cursor;
    }

    enableDrawCursor() {
        const canvas = this.map?.getCanvas?.();
        const container = this.getCanvasContainer();

        if (canvas == null || container == null || typeof document === 'undefined') {
            return;
        }

        const cursor = this.ensureDrawCursorElement();

        if (cursor.parentElement !== container) {
            container.appendChild(cursor);
        }

        canvas.style.cursor = 'none';
        container.style.cursor = 'none';

        if (this.drawCursorMoveHandler == null) {
            this.drawCursorMoveHandler = (event) => {
                const rect = container.getBoundingClientRect();
                cursor.style.display = 'block';
                cursor.style.left = `${event.clientX - rect.left}px`;
                cursor.style.top = `${event.clientY - rect.top}px`;
            };
            this.drawCursorEnterHandler = () => {
                cursor.style.display = 'block';
            };
            this.drawCursorLeaveHandler = () => {
                cursor.style.display = 'none';
            };

            container.addEventListener('mousemove', this.drawCursorMoveHandler);
            container.addEventListener('mouseenter', this.drawCursorEnterHandler);
            container.addEventListener('mouseleave', this.drawCursorLeaveHandler);
        }
    }

    disableDrawCursor() {
        const canvas = this.map?.getCanvas?.();
        const container = this.getCanvasContainer();

        if (canvas != null) {
            canvas.style.cursor = 'unset';
        }

        if (container != null) {
            container.style.cursor = 'unset';

            if (this.drawCursorMoveHandler != null) {
                container.removeEventListener('mousemove', this.drawCursorMoveHandler);
                container.removeEventListener('mouseenter', this.drawCursorEnterHandler);
                container.removeEventListener('mouseleave', this.drawCursorLeaveHandler);
            }
        }

        if (this.drawCursorElement != null) {
            this.drawCursorElement.remove();
        }

        this.drawCursorElement = null;
        this.drawCursorMoveHandler = null;
        this.drawCursorEnterHandler = null;
        this.drawCursorLeaveHandler = null;
    }

    mode(_mode) {
        if (this.isDrawing() == false) {
            throw new Error("You can't set a mode if its not drawing");
        }

        this._mode = _mode;

        let desired_mode;
        if (_mode == DrawTools.POLYGON) {
            desired_mode = "draw_polygon";
        } else if (_mode == DrawTools.CIRCLE) {
            desired_mode = "drag_circle";
        } else if (_mode == DrawTools.POINT) {
            desired_mode = "draw_point";
        }
        
        this.draw.changeMode(desired_mode);

        if ([DrawTools.POLYGON, DrawTools.CIRCLE, DrawTools.POINT].includes(_mode)) {
            this.enableDrawCursor();
        } else {
            this.disableDrawCursor();
        }
    }

    suppressCreate() {
        this.suppressNextCreate = true;

        if (this.suppressCreateTimer != null) {
            window.clearTimeout(this.suppressCreateTimer);
        }

        this.suppressCreateTimer = window.setTimeout(() => {
            this.suppressNextCreate = false;
            this.suppressCreateTimer = null;
        }, 350);
    }

    clearCreateSuppression() {
        this.suppressNextCreate = false;

        if (this.suppressCreateTimer != null) {
            window.clearTimeout(this.suppressCreateTimer);
            this.suppressCreateTimer = null;
        }
    }

    cancelMode() {
        const shouldTrashActiveDraft = [DrawTools.POLYGON, DrawTools.CIRCLE, DrawTools.POINT].includes(this._mode);

        this._mode = null;
        this.disableDrawCursor();

        if (this.draw != null) {
            this.suppressCreate();

            if (shouldTrashActiveDraft) {
                this.draw.trash();
            }

            this.draw.changeMode('simple_select');
        }
    }

    directSelect(featureId) {
        this.draw.changeMode('direct_select', { featureId });
    }
    
    deleteLayer(layerId) {
        this.draw.delete(layerId);
    }

    deleteAll() {
        if (this.draw != null) {
            this.draw.deleteAll();
        }
    }

    getAll() {
        return this.draw.getAll();
    }

    isDrawing() {
        return this.status == DrawStatus.DRAWING;
    }

    acceptPolygon() {
        return this.enabledTools.indexOf(DrawTools.POLYGON) != -1;
    }

    acceptCircle() {
        return this.enabledTools.indexOf(DrawTools.CIRCLE) != -1;
    }

    acceptFile() {
        return this.enabledTools.indexOf(DrawTools.UPLOAD_FILE) != -1;
    }

    acceptPoint() {
        return this.enabledTools.indexOf(DrawTools.POINT) != -1;
    }
};

export const drawState = new DrawState();
